import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import './PickListImport.css';

const PickListImport = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const inputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const applyPdfFile = useCallback((selected) => {
    if (!selected) return;
    const name = (selected.name || '').toLowerCase();
    if (!name.endsWith('.pdf')) {
      setError('Please select a PDF file.');
      setFile(null);
      setFileName('');
      return;
    }
    setFile(selected);
    setFileName(selected.name);
    setError('');
    setResult(null);
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    applyPdfFile(selected);
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    handleDrag(e);
    dragCounterRef.current += 1;
    if (e.dataTransfer?.types?.includes('Files')) setDragActive(true);
  };

  const handleDragOut = (e) => {
    handleDrag(e);
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    handleDrag(e);
    dragCounterRef.current = 0;
    setDragActive(false);
    const dropped = e.dataTransfer.files?.[0];
    applyPdfFile(dropped);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a PDF file.');
      return;
    }
    setIsUploading(true);
    setError('');
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getToken();
      const res = await fetch('/api/pick-lists/import', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Import failed');
      }
      setResult(data);
      setFile(null);
      setFileName('');
    } catch (err) {
      setError(err.message || 'Failed to import pick list.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleBack = () => navigate('/pick-list');

  return (
    <Layout>
      <div className="pick-list-import">
        <div className="pick-list-import-head">
          <Button
            type="button"
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ alignSelf: 'flex-start', mb: 1, textTransform: 'none', fontWeight: 600 }}
          >
            Back
          </Button>
          <h1 className="home-menu-title pick-list-import-title">Pick List Import</h1>
          <p className="home-menu-intro pick-list-import-intro">
            Upload a PDF pick list (Isuzu format). Part number, description, qty, and bin location will be
            extracted.
          </p>
        </div>

        <div className="pick-list-import-panel">
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,application/pdf"
            className="pick-list-import-input-hidden"
            onChange={handleFileChange}
            disabled={isUploading}
            aria-hidden
          />
          <div
            className={`pick-list-import-dropzone${dragActive ? ' pick-list-import-dropzone--active' : ''}${
              isUploading ? ' pick-list-import-dropzone--disabled' : ''
            }`}
            role="button"
            tabIndex={0}
            onClick={() => !isUploading && inputRef.current?.click()}
            onKeyDown={(e) => {
              if (!isUploading && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragEnter={handleDragIn}
            onDragLeave={handleDragOut}
            onDragOver={handleDragIn}
            onDrop={handleDrop}
            aria-label="Drop PDF here or click to browse"
          >
            <CloudUploadOutlinedIcon className="pick-list-import-drop-icon" aria-hidden />
            <span className="pick-list-import-drop-title">Drop PDF here</span>
            <span className="pick-list-import-drop-hint">
              or{' '}
              <button
                type="button"
                className="pick-list-import-browse-link"
                disabled={isUploading}
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
              >
                browse files
              </button>
            </span>
          </div>

          {fileName && (
            <div className="pick-list-import-file-row">
              <InsertDriveFileOutlinedIcon className="pick-list-import-file-icon" fontSize="small" />
              <span className="pick-list-import-file-name">{fileName}</span>
              <button
                type="button"
                className="pick-list-import-file-clear"
                disabled={isUploading}
                onClick={() => {
                  setFile(null);
                  setFileName('');
                  setError('');
                }}
              >
                Remove
              </button>
            </div>
          )}

          {error && <div className="pick-list-import-error">{error}</div>}

          <Button
            type="button"
            variant="contained"
            size="large"
            disabled={!file || isUploading}
            onClick={handleUpload}
            sx={{ mt: 2, alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, px: 3, py: 1.25 }}
          >
            {isUploading ? 'Importing…' : 'Import pick list'}
          </Button>
        </div>

        {result?.pickList && (
          <div className="pick-list-import-result">
            <h3 className="pick-list-import-result-title">Pick list created</h3>
            <p className="pick-list-import-result-meta">
              OBD: {result.pickList.obdNumber || '—'} / {result.pickList.obdDate || '—'} · Order:{' '}
              {result.pickList.orderNumber || '—'} · Customer: {result.pickList.customerNumber || '—'}
            </p>
            <p className="pick-list-import-result-lines">
              <strong>{result.pickList.lines?.length ?? 0}</strong> line(s) imported.
            </p>
            <Button
              type="button"
              variant="outlined"
              color="success"
              onClick={() => navigate('/pick-list/list')}
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              View all pick lists
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PickListImport;
